import React, { useState, useEffect } from "react";
import { Frame } from "../../components/layouts/Frame";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Grid,
  Divider,
  SelectChangeEvent,
  FormHelperText,
  Snackbar,
  Alert,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";

import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../contexts/AuthContext";

interface UserData {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  type: "deckboard" | "ecodek" | "";
  permissions: {
    adminAccess: boolean;
    webAdminAccess: boolean;
    userManagement: boolean;
    editPrices: boolean;
  };
}

const defaultUser: UserData = {
  id: "",
  fullName: "",
  email: "",
  type: "",
  permissions: {
    adminAccess: false,
    webAdminAccess: false,
    userManagement: false,
    editPrices: false,
  },
  phoneNumber: "",
};

interface Payload {
  id: string;
  name: string;
  type: string;
  roles: number[];
  email?: string;
  phone_number: string;
}

const UserEditPage: React.FC = () => {
  const { fetchData } = useAPI();

  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserData>(defaultUser);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (userId) {
      setLoading(true);
      getUserById();
    }
  }, [userId]);

  const getUserById = async () => {
    const { errors, data } = await fetchData(`/user/get/${userId}`);

    if (data && userId) {
      const roles = data?.roles ?? [];

      setUser({
        id: userId,
        fullName: data?.name ?? "",
        email: data?.email ?? "",
        type: data?.type ?? "",
        permissions: {
          adminAccess: roles.includes(0),
          webAdminAccess: roles.includes(1),
          userManagement: roles.includes(2),
          editPrices: roles.includes(3),
        },
        phoneNumber: data?.phone_number ?? "",
      });
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!user.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!user.email.trim() && !userId) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(user.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!user.type) {
      newErrors.type = "Type is required";
    }

    if (!user.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange =
    (permission: keyof UserData["permissions"]) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUser(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permission]: e.target.checked,
        },
      }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      console.log("Saving user data:", user);

      let roles = [];

      if (user.permissions.adminAccess) {
        roles.push(0);
      }

      if (user.permissions.webAdminAccess) {
        roles.push(1);
      }

      if (user.permissions.userManagement) {
        roles.push(2);
      }

      if (user.permissions.editPrices) {
        roles.push(3);
      }

      let payload: Payload = {
        id: userId ?? "",
        name: user.fullName,
        type: user.type,
        roles,
        phone_number: user.phoneNumber,
      };

      if (!userId) {
        payload.email = user.email;
      }

      const { errors, data } = await fetchData("/user/edit", payload, "POST");

      if (errors) {
        setSnackbar({
          open: true,
          message: errors?.email ?? "",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: "User information saved successfully!",
          severity: "success",
        });
      }

      setTimeout(() => {
        navigate("/users");
      }, 1500);
    }
  };

  const handleCancel = () => {
    navigate("/users");
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Frame title="Users">
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mb: 2 }}>
          Back to Users
        </Button>

        <Card elevation={3}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <PersonIcon sx={{ fontSize: 30, mr: 2, color: "primary.main" }} />
              <Typography variant="h5" component="h1">
                {userId ? "Edit User" : "Create New User"}
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Full Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Full Name"
                    name="fullName"
                    value={user.fullName}
                    onChange={handleTextChange}
                    fullWidth
                    required
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={user.email}
                    onChange={handleTextChange}
                    fullWidth
                    required
                    disabled={userId ? true : false}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>

                {/* Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!errors.type}>
                    <InputLabel id="user-type-label">User Type</InputLabel>
                    <Select
                      labelId="user-type-label"
                      id="user-type"
                      name="type"
                      value={user.type}
                      label="User Type"
                      onChange={handleSelectChange}>
                      <MenuItem value="deckboard">Dekboard</MenuItem>
                      <MenuItem value="ecodek">Ecodek</MenuItem>
                    </Select>
                    {errors.type && (
                      <FormHelperText error={true}>
                        {errors.type}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Phone number"
                    name="phoneNumber"
                    value={user.phoneNumber}
                    onChange={handleTextChange}
                    fullWidth
                    required
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  User Permissions
                </Typography>
                <Divider />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={user.permissions.adminAccess}
                            onChange={handlePermissionChange("adminAccess")}
                          />
                        }
                        label="Admin level access on the app"
                      />
                      <FormHelperText>
                        Grants full administrative privileges within the mobile
                        application
                      </FormHelperText>
                    </FormGroup>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={user.permissions.webAdminAccess}
                            onChange={handlePermissionChange("webAdminAccess")}
                          />
                        }
                        label="Access to web admin"
                      />
                      <FormHelperText>
                        Allows user to access the web administration portal
                      </FormHelperText>
                    </FormGroup>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={user.permissions.userManagement}
                            onChange={handlePermissionChange("userManagement")}
                          />
                        }
                        label="Add/edit users"
                      />
                      <FormHelperText>
                        Enables user to create new accounts and modify existing
                        user details
                      </FormHelperText>
                    </FormGroup>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={user.permissions.editPrices}
                            onChange={handlePermissionChange("editPrices")}
                          />
                        }
                        label="Edit Prices"
                      />
                      <FormHelperText>
                        Allows user to modify product pricing throughout the
                        system
                      </FormHelperText>
                    </FormGroup>
                  </Card>
                </Grid>
              </Grid>

              {errors.permission && (
                <FormHelperText error={true}>
                  {errors.permission}
                </FormHelperText>
              )}

              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}>
                  Save User
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Frame>
  );
};

export default UserEditPage;
